import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, URGENCY, ISSUE_STATUS, BUSINESS_AREAS } from "@/lib/format";
import { toast } from "sonner";

const ISSUE_STATUSES = ["open", "under_review", "decision_required", "in_progress", "resolved", "closed"];

export default function Issues() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const [issues, setIssues] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  const load = () => {
    api.get(`/issues?org_id=${orgId}`).then((r) => setIssues(r.data));
    api.get(`/decisions?org_id=${orgId}`).then((r) => setDecisions(r.data));
  };
  useEffect(() => { if (orgId) { setIssues(null); setDecisions(null); load(); } }, [orgId]);

  if (!issues || !decisions) return <PageLoading />;

  return (
    <div data-testid="issues-page">
      <PageHeader eyebrow="Governance" title="Issues & Decisions" subtitle="Track blockers and record the decisions that move the business forward." icon="TriangleAlert" />

      <Tabs defaultValue="issues">
        <TabsList className="mb-5 bg-[#E4E8EF]">
          <TabsTrigger value="issues" data-testid="tab-issues">Issues ({issues.length})</TabsTrigger>
          <TabsTrigger value="decisions" data-testid="tab-decisions">Decisions ({decisions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="issues">
          {canEdit && <div className="mb-4 flex justify-end"><Button onClick={() => setIssueOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-issue-btn"><Icons.Plus size={16} className="mr-1.5" /> Raise issue</Button></div>}
          {issues.length === 0 ? <EmptyState icon="TriangleAlert" title="No issues" description="Raise an issue to track blockers and decisions." /> : (
            <div className="space-y-3">
              {issues.map((i) => (
                <div key={i.id} onClick={() => setSelectedIssue(i)} className="amden-card amden-card-hover cursor-pointer p-5" data-testid={`issue-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <RagBadge rag={ISSUE_STATUS[i.status]?.rag}>{ISSUE_STATUS[i.status]?.label}</RagBadge>
                        <RagBadge rag={URGENCY[i.urgency]?.rag} dot={false}>{URGENCY[i.urgency]?.label}</RagBadge>
                        <span className="text-xs text-[#1E2D44]/50">{i.business_area}</span>
                      </div>
                      <h3 className="mt-2 font-display text-base font-bold text-[#0B1729]">{i.title}</h3>
                      <p className="mt-1 text-sm text-[#1E2D44]/60 line-clamp-2">{i.description}</p>
                    </div>
                    <div className="shrink-0 text-right text-xs text-[#1E2D44]/50">
                      <div>Owner: {i.owner_name || "—"}</div>
                      {i.target_date && <div>Target {fmtDate(i.target_date)}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decisions">
          {canEdit && <div className="mb-4 flex justify-end"><Button onClick={() => setDecisionOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-decision-btn"><Icons.Plus size={16} className="mr-1.5" /> Log decision</Button></div>}
          {decisions.length === 0 ? <EmptyState icon="GitBranch" title="No decisions logged" description="Record key decisions for a clear audit trail." /> : (
            <div className="space-y-3">
              {decisions.map((d) => (
                <div key={d.id} className="amden-card p-5" data-testid={`decision-${d.id}`}>
                  <div className="flex items-start justify-between">
                    <h3 className="font-display text-base font-bold text-[#0B1729]">{d.title}</h3>
                    <span className="text-xs text-[#1E2D44]/50">{fmtDate(d.created_at)}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <DField label="Decision made" value={d.decision_made} />
                    <DField label="Owner" value={d.owner_name} />
                    <DField label="Reasoning" value={d.reasoning} />
                    <DField label="Expected outcome" value={d.expected_outcome} />
                    {d.review_date && <DField label="Review date" value={fmtDate(d.review_date)} />}
                    {d.actual_outcome && <DField label="Actual outcome" value={d.actual_outcome} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {issueOpen && <IssueDialog orgId={orgId} onClose={() => setIssueOpen(false)} onSaved={() => { setIssueOpen(false); load(); }} />}
      {decisionOpen && <DecisionDialog orgId={orgId} onClose={() => setDecisionOpen(false)} onSaved={() => { setDecisionOpen(false); load(); }} />}
      {selectedIssue && <IssueDetail issue={selectedIssue} canEdit={canEdit} onClose={() => setSelectedIssue(null)} onSaved={() => { setSelectedIssue(null); load(); }} />}
    </div>
  );
}

function DField({ label, value }) {
  if (!value) return null;
  return <div><div className="label-eyebrow mb-0.5">{label}</div><p className="text-[#1E2D44]/80">{value}</p></div>;
}

function IssueDetail({ issue, canEdit, onClose, onSaved }) {
  const [status, setStatus] = useState(issue.status);
  const [resolution, setResolution] = useState(issue.resolution || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/issues/${issue.id}`, { status, resolution: resolution || null }); toast.success("Issue updated"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2"><RagBadge rag={URGENCY[issue.urgency]?.rag}>{URGENCY[issue.urgency]?.label} urgency</RagBadge></div>
          <DialogTitle className="font-display text-xl">{issue.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <DField label="Description" value={issue.description} />
          <div className="grid grid-cols-2 gap-4">
            <DField label="Business area" value={issue.business_area} />
            <DField label="Owner" value={issue.owner_name} />
            <DField label="Business impact" value={issue.business_impact} />
            {issue.financial_impact != null && <DField label="Financial impact" value={issue.financial_impact} />}
            <DField label="Proposed solution" value={issue.proposed_solution} />
            <DField label="Decision required" value={issue.decision_required} />
          </div>
          {canEdit && (
            <div className="rounded-xl border border-[#E4E8EF] p-4">
              <Label className="text-xs">Update status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="issue-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ISSUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{ISSUE_STATUS[s].label}</SelectItem>)}</SelectContent>
              </Select>
              <Label className="mt-3 block text-xs">Resolution</Label>
              <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} className="mt-1" placeholder="How was this resolved?" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-issue-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ orgId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", business_area: BUSINESS_AREAS[0], urgency: "medium", business_impact: "", proposed_solution: "", decision_required: "", target_date: "", status: "open" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try { await api.post(`/issues?org_id=${orgId}`, form); toast.success("Issue raised"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Raise an issue</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="issue-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Business area</Label>
              <Select value={form.business_area} onValueChange={(v) => setForm({ ...form, business_area: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(URGENCY).map((u) => <SelectItem key={u} value={u}>{URGENCY[u].label}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><Label>Proposed solution</Label><Textarea value={form.proposed_solution} onChange={(e) => setForm({ ...form, proposed_solution: e.target.value })} className="mt-1" /></div>
          <div><Label>Target resolution date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-issue-create">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Raise issue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ orgId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", context: "", options_considered: "", decision_made: "", reasoning: "", expected_outcome: "", review_date: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try { await api.post(`/decisions?org_id=${orgId}`, form); toast.success("Decision logged"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Log a decision</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="decision-title-input" /></div>
          <div><Label>Context</Label><Textarea value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="mt-1" /></div>
          <div><Label>Decision made</Label><Textarea value={form.decision_made} onChange={(e) => setForm({ ...form, decision_made: e.target.value })} className="mt-1" /></div>
          <div><Label>Reasoning</Label><Textarea value={form.reasoning} onChange={(e) => setForm({ ...form, reasoning: e.target.value })} className="mt-1" /></div>
          <div><Label>Review date</Label><Input type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-decision-create">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Log decision"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
